/**
 * A/B Test Hook — Paywall CTR experiment
 *
 * Assigns each user deterministically to variant 'A' (control) or 'B' (challenger)
 * using localStorage for persistence across page loads.
 *
 * Variants:
 *   A — Control: current modal (title "⭐ Opgrader til Premium", price block, feature table)
 *   B — Challenger: urgency headline, single benefit hook, social proof, condensed CTA
 *
 * GA4 tracking: fires `ab_test_exposure` on first exposure, and attaches
 * `ab_variant` parameter to all view_promotion / select_promotion events.
 */

import { useEffect, useState } from 'react';
import { sendGa4AbEvent } from '../utils/analyticsAB';

const EXPERIMENT_KEY = 'paywall_ctr_v1';
const VARIANTS = ['A', 'B'];

function assignVariant() {
  const stored = localStorage.getItem(EXPERIMENT_KEY);
  if (stored && VARIANTS.includes(stored)) return stored;
  const assigned = Math.random() < 0.5 ? 'A' : 'B';
  localStorage.setItem(EXPERIMENT_KEY, assigned);
  return assigned;
}

export function useABTest() {
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    const v = assignVariant();
    setVariant(v);
    // Fire exposure event once per session
    const sessionKey = `${EXPERIMENT_KEY}_exposed`;
    if (!sessionStorage.getItem(sessionKey)) {
      sendGa4AbEvent('ab_test_exposure', { ab_experiment: EXPERIMENT_KEY, ab_variant: v });
      sessionStorage.setItem(sessionKey, '1');
    }
  }, []);

  return variant;
}
